import { MouseEvent } from 'react';
import { FaCubes, FaPython } from 'react-icons/fa6';
import { DeleteOutline, FormatListBulleted, Link as LinkIcon } from '@mui/icons-material';
import { PythonFunctionSummary } from 'api/model';
import ScopeIndicator from 'components/indicators/ScopeIndicator';
import usePythonFunction from 'api/python-functions/usePythonFunction';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import useDialog from 'hooks/useDialog';
import { Typography } from '@mui/material';
import { toast } from 'react-toastify';
import PythonFunctionUsageDialog from 'components/dialog/PythonFunctionUsageDialog';
import { ActionIconButton } from 'components/button/ActionIconButton';
import { EntityCard } from 'components/settingsHub/EntityCard';

type Props = {
  pythonFunction: PythonFunctionSummary;
  onSelect: () => void;
};

const PythonFunctionItem = ({ pythonFunction, onSelect }: Props) => {
  const { remove } = usePythonFunction({ functionId: pythonFunction.id, enabled: false });
  const [showDeleteDialog, DeleteDialog] = useDialog(ConfirmationDialog);
  const [showUsageDialog, UsageDialog] = useDialog(PythonFunctionUsageDialog);

  const isGlobal = !pythonFunction.project_id;
  const libsCount = pythonFunction.extra_libs?.length ?? 0;

  const stop = (fn: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  const handleCopyUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('functionId', pythonFunction.id);
    void navigator.clipboard.writeText(url.toString()).then(() => toast.success('Function link copied to clipboard'));
  };

  return (
    <EntityCard onClick={onSelect} ariaLabel={`Open function ${pythonFunction.name}`} minHeight={224}>
      <ScopeIndicator isGlobal={isGlobal} className="absolute left-3 top-3 z-10" />
      <div className="entity-card-actions absolute top-2 right-2 z-10 flex gap-1 opacity-0 transition-opacity">
        <ActionIconButton size="small" color="info" title="View usage" onClick={stop(() => showUsageDialog(true))}>
          <FormatListBulleted fontSize="small" />
        </ActionIconButton>
        <ActionIconButton size="small" color="info" title="Copy link" onClick={stop(handleCopyUrl)}>
          <LinkIcon fontSize="small" />
        </ActionIconButton>
        <ActionIconButton size="small" color="error" title="Delete" onClick={stop(() => showDeleteDialog(true))}>
          <DeleteOutline fontSize="small" />
        </ActionIconButton>
      </div>

      <div className="flex h-full flex-col items-center justify-evenly gap-2 py-3 text-center">
        <FaPython
          size={46}
          className={isGlobal ? 'text-yellow-500 dark:text-yellow-400' : 'text-sky-500 dark:text-sky-400'}
        />
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }} className="line-clamp-2 px-3">
          {pythonFunction.name}
        </Typography>
        {pythonFunction.description && (
          <Typography variant="body2" color="text.secondary" className="line-clamp-2 px-3">
            {pythonFunction.description}
          </Typography>
        )}
        <div className="flex items-center justify-center gap-4">
          <Typography variant="body2" color="text.secondary">
            rev {pythonFunction.revision}
          </Typography>
          {libsCount > 0 && (
            <span className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
              <FaCubes size={15} />
              {libsCount} lib{libsCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      <DeleteDialog
        title="Delete Python Function"
        onConfirm={() => remove.mutate()}
        buttonsSettings={{ confirmText: 'Delete', confirmColor: 'error' }}
      >
        <Typography>
          Are you sure you want to delete the function <strong>{pythonFunction.name}</strong>? This action cannot be
          undone.
        </Typography>
      </DeleteDialog>
      <UsageDialog pythonFunction={pythonFunction} onClose={() => showUsageDialog(false)} />
    </EntityCard>
  );
};

export default PythonFunctionItem;
